import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { getDataAPI, putDataAPI } from '../lib/api'
import Button from '../components/ui/Button'
import Alert from '../components/ui/Alert'

const ALL_APPS = [
  { id: 'workflows', name: 'Workflows', abbr: 'Wf', color: '#FF5252', description: 'Workflow automation' },
  { id: 'bookings', name: 'Bookings', abbr: 'Boo', color: '#FFD93D', description: 'Manage bookings and shows' },
  { id: 'catalog', name: 'Catalog', abbr: 'Ca', color: '#4DB8C4', description: 'Manage music catalog' },
  { id: 'tourmngr', name: 'TourMngr', abbr: 'TM', color: '#FF8C42', description: 'Tour management' },
  { id: 'notes', name: 'Notes', abbr: 'Not', color: '#A855F7', description: 'Notes and documentation' },
  { id: 'connectors', name: 'Connectors', abbr: 'Cnr', color: '#5B7EBD', description: 'Third-party integrations' },
  { id: 'analytics', name: 'Analytics', abbr: 'An', color: '#10B981', description: 'View performance data' },
  { id: 'tasks', name: 'Tasks', abbr: 'Tsk', color: '#FF6B35', description: 'Task management' },
  { id: 'knowledge', name: 'Knowledge', abbr: 'Kw', color: '#9F7AEA', description: 'Knowledge base' },
  { id: 'contacts', name: 'Contacts', abbr: 'Cnt', color: '#B08A4C', description: 'Contact management' },
  { id: 'ai', name: 'AI', abbr: 'AI', color: '#6366F1', description: 'AI assistant and automation' },
]

type AccessLevel = 'view' | 'edit'
type DefaultPermissions = {
  accessLevel: AccessLevel
  apps: string[]
  appPermissions: Record<string, AccessLevel>
  aiUsageLimit: number
}

const DEFAULT_PERMISSIONS: Record<'manager' | 'support', DefaultPermissions> = {
  manager: {
    accessLevel: 'edit',
    apps: ['workflows', 'bookings', 'catalog', 'tourmngr', 'notes', 'connectors', 'analytics', 'tasks', 'knowledge', 'contacts', 'ai'],
    appPermissions: {
      workflows: 'edit',
      bookings: 'edit',
      catalog: 'edit',
      tourmngr: 'edit',
      notes: 'edit',
      connectors: 'edit',
      analytics: 'view',
      tasks: 'edit',
      knowledge: 'edit',
      contacts: 'edit',
      ai: 'edit',
    },
    aiUsageLimit: 5000,
  },
  support: {
    accessLevel: 'view',
    apps: [],
    appPermissions: {},
    aiUsageLimit: 0,
  },
}

export default function TeamMemberEditPage() {
  const { teamMemberId } = useParams<{ teamMemberId: string }>()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [teamMember, setTeamMember] = useState<any>(null)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)

  const [accessLevel, setAccessLevel] = useState<'view' | 'edit'>('view')
  const [selectedApps, setSelectedApps] = useState<string[]>([])
  const [appPermissions, setAppPermissions] = useState<Record<string, 'view' | 'edit'>>({})
  const [aiUsageLimit, setAiUsageLimit] = useState<number>(1000)
  const [financialAccess, setFinancialAccess] = useState<boolean>(true)
  const [showFinancialToggle, setShowFinancialToggle] = useState<boolean>(false)

  const token = (() => {
    try {
      const t = localStorage.getItem('dizeeToken')
      if (!t) return null
      const parsed = JSON.parse(t)
      return typeof parsed === 'string' ? parsed : parsed
    } catch {
      return localStorage.getItem('dizeeToken')
    }
  })()

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (openDropdown && !(e.target as HTMLElement).closest('.relative')) {
        setOpenDropdown(null)
      }
    }
    if (openDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [openDropdown])

  useEffect(() => {
    const fetchTeamMember = async () => {
      if (!teamMemberId || !token) return
      try {
        setLoading(true)
        const res = await getDataAPI(`team/member/${teamMemberId}`, token)
        const data = res?.data?.data ?? res?.data
        if (data) {
          setTeamMember(data)
          const permissions = data.permissions || {}
          setAccessLevel(permissions.accessLevel || 'view')
          setSelectedApps(permissions.apps || [])
          setAppPermissions(permissions.appPermissions || {})
          setAiUsageLimit(permissions.aiUsageLimit || 1000)
          const accountType = data.role || data.accountType
          setShowFinancialToggle(accountType === 'manager')
          setFinancialAccess(data.financialAccess !== false)
        } else {
          setError('Failed to load team member')
        }
      } catch (e: any) {
        setError(e?.response?.data?.message || e?.message || 'Failed to load team member')
      } finally {
        setLoading(false)
      }
    }
    fetchTeamMember()
  }, [teamMemberId, token])

  const handleAppToggle = (appId: string) => {
    setSelectedApps((prev) => {
      const newSelection = prev.includes(appId) ? prev.filter((id) => id !== appId) : [...prev, appId]
      if (!newSelection.includes(appId)) {
        setAppPermissions((prevPerms) => {
          const newPerms = { ...prevPerms }
          delete newPerms[appId]
          return newPerms
        })
      } else {
        setAppPermissions((prevPerms) => ({
          ...prevPerms,
          [appId]: appId === 'analytics' ? 'view' : 'edit',
        }))
      }
      return newSelection
    })
  }

  const handleAppPermissionChange = (appId: string, permission: 'view' | 'edit') => {
    const finalPermission = teamMember?.role === 'support' && permission === 'edit' ? 'view' : permission
    setAppPermissions((prev) => ({ ...prev, [appId]: finalPermission }))
  }

  const handleSave = async () => {
    if (!token) return
    setSaving(true)
    setError(null)
    try {
      const permissionsData = {
        teamMemberId,
        permissions: {
          accessLevel: teamMember?.role === 'support' ? 'view' : accessLevel,
          apps: selectedApps,
          appPermissions,
          aiUsageLimit: parseInt(String(aiUsageLimit)),
        },
      }
      const res = await putDataAPI('team/member/permissions', permissionsData, token)
      if (showFinancialToggle && (res?.status === 200 || res?.data?.status === 200)) {
        await putDataAPI('team/member/financial-access', { teamMemberId, financialAccess }, token)
      }
      if (res?.status === 200 || res?.data?.status === 200) {
        setSuccess('Team member permissions updated successfully')
        setTimeout(() => navigate('/team'), 2000)
      } else {
        setError(res?.message || res?.data?.message || 'Failed to update permissions')
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to update permissions')
    } finally {
      setSaving(false)
    }
  }

  const handleResetDefaults = () => {
    if (teamMember) {
      const roleKey: keyof typeof DEFAULT_PERMISSIONS =
        teamMember.role === 'support' || teamMember.role === 'manager' ? teamMember.role : 'manager'
      const defaults = DEFAULT_PERMISSIONS[roleKey]
      setAccessLevel(defaults.accessLevel)
      setSelectedApps(defaults.apps)
      setAppPermissions(defaults.appPermissions)
      setAiUsageLimit(defaults.aiUsageLimit)
    }
  }

  const getInitials = (name: string) => {
    return name
      ?.split(' ')
      .map((w) => w[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || 'TM'
  }

  if (loading) {
    return (
      <div className="p-4 pt-16 md:pt-8 md:p-8 max-w-6xl mx-auto">
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
      </div>
    )
  }

  if (!teamMember) {
    return (
      <div className="p-4 pt-16 md:pt-8 md:p-8 max-w-6xl mx-auto">
        <p className="text-sm text-red-500 dark:text-red-400">Team member not found</p>
      </div>
    )
  }

  return (
    <div className="p-4 pt-4 md:p-8 max-w-6xl mx-auto">
      {/* Sticky page header */}
      <div className="sticky top-0 z-10 -mx-4 md:-mx-8 px-4 md:px-8 pt-16 md:pt-0 pb-4 bg-[rgb(var(--bg-rgb))]">
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-start">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight mb-2 text-gray-900 dark:text-white">
              Edit Team Member
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Manage permissions and access for {teamMember.name || teamMember.email}
            </p>
          </div>
          <div className="flex gap-3">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
            <button
              type="button"
              onClick={() => navigate('/team')}
              className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white font-medium"
            >
              Cancel
          </button>
        </div>
      </div>
      </div>

      {error && (
        <div className="mb-6">
          <Alert type="error" message={error} onDismiss={() => setError(null)} />
        </div>
      )}
      {success && (
        <div className="mb-6">
          <Alert type="success" message={success} onDismiss={() => setSuccess(null)} />
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <div className="flex items-center gap-4">
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center text-white text-lg font-semibold"
            style={{ background: 'linear-gradient(45deg, #4CAF50, #66BB6A)' }}
          >
            {teamMember.profilePicture ? (
              <img
                src={teamMember.profilePicture}
                alt=""
                className="w-full h-full rounded-full object-cover"
              />
            ) : (
              getInitials(teamMember.name || teamMember.email)
            )}
          </div>
          <div>
            <div className="text-lg font-semibold text-gray-900 dark:text-white">
              {teamMember.name || teamMember.email}
            </div>
            <span
              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                teamMember.role === 'manager'
                  ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300'
                  : 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
              }`}
            >
              {teamMember.role === 'manager' ? 'Manager' : 'Support'}
            </span>
          </div>
        </div>
      </div>

      {/* App Permissions */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <h2 className="text-sm font-medium mb-4 text-gray-900 dark:text-white">App Permissions</h2>
        <div className="space-y-2">
          {ALL_APPS.filter((app) => !(app.id === 'ai' && teamMember.role === 'support')).map((app) => {
            const isVisible = selectedApps.includes(app.id)
            const permissionLevel =
              teamMember.role === 'support' ? 'view' : appPermissions[app.id] || 'view'
            return (
              <div key={app.id} className="flex items-center justify-between py-3">
                <div className="flex items-center gap-3">
                  <div
                    className="w-8 h-8 rounded flex items-center justify-center text-white text-xs font-semibold"
                    style={{ backgroundColor: app.color }}
                  >
                    {app.abbr}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white">{app.name}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {!isVisible ? 'Hidden' : permissionLevel === 'edit' ? 'Edit access' : 'View only'}
                    </div>
                  </div>
                </div>
                <div className="relative">
                  <button
                    onClick={() => setOpenDropdown(openDropdown === app.id ? null : app.id)}
                    className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-400 focus:ring-offset-1 rounded p-1 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                    </svg>
                  </button>
                  {openDropdown === app.id && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setOpenDropdown(null)} />
                      <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-20 py-1">
                      {app.id === 'ai' ? (
                        <>
                          {teamMember.role !== 'support' && (
                            <button
                              onClick={() => {
                                if (!isVisible) {
                                  setSelectedApps((prev) => [...prev, app.id])
                                  setAppPermissions((prev) => ({ ...prev, [app.id]: 'edit' }))
                                } else {
                                  handleAppPermissionChange(app.id, 'edit')
                                }
                                setOpenDropdown(null)
                              }}
                              className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                            >
                              Edit access
                            </button>
                          )}
                          <button
                            onClick={() => {
                              if (isVisible) handleAppToggle(app.id)
                              setOpenDropdown(null)
                            }}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                          >
                            Hide
                          </button>
                        </>
                      ) : (
                        <>
                          {teamMember.role !== 'support' && (
                            <button
                              onClick={() => {
                                if (!isVisible) {
                                  setSelectedApps((prev) => [...prev, app.id])
                                  setAppPermissions((prev) => ({ ...prev, [app.id]: 'edit' }))
                                } else {
                                  handleAppPermissionChange(app.id, 'edit')
                                }
                                setOpenDropdown(null)
                              }}
                              className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                            >
                              Edit access
                            </button>
                          )}
                          <button
                            onClick={() => {
                              if (!isVisible) {
                                setSelectedApps((prev) => [...prev, app.id])
                                setAppPermissions((prev) => ({ ...prev, [app.id]: 'view' }))
                              } else {
                                handleAppPermissionChange(app.id, 'view')
                              }
                              setOpenDropdown(null)
                            }}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                          >
                            View access
                          </button>
                          <button
                            onClick={() => {
                              if (isVisible) handleAppToggle(app.id)
                              setOpenDropdown(null)
                            }}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                          >
                            Hide
                          </button>
                        </>
                      )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Financial Access - Manager only */}
      {showFinancialToggle && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-medium mb-1 text-gray-900 dark:text-white">Financial Access</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Allow access to financial data in Bookings, Tour Manager, and Analytics
              </p>
            </div>
            <button
              onClick={() => setFinancialAccess(!financialAccess)}
              className={`w-11 h-6 rounded-full transition-all ${
                financialAccess ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <div
                className={`w-5 h-5 rounded-full bg-white transition-transform ${
                  financialAccess ? 'translate-x-5' : 'translate-x-0.5'
                }`}
              ></div>
            </button>
          </div>
        </div>
      )}

      {/* AI Usage Limit */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <h2 className="text-sm font-medium mb-4 text-gray-900 dark:text-white">AI Usage Limit (monthly)</h2>
        <input
          type="number"
          value={aiUsageLimit}
          onChange={(e) => setAiUsageLimit(Number(e.target.value))}
          placeholder="Enter monthly AI query limit"
          className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-400 focus:border-transparent"
          min="0"
        />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Set to 0 for unlimited usage</p>
      </div>

      {/* Reset Defaults */}
      <div className="text-center">
        <button
          onClick={handleResetDefaults}
          className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white font-medium"
        >
          Reset to role defaults ({teamMember.role})
        </button>
      </div>
    </div>
  )
}
